import { Box, Checkbox, FormControlLabel, MenuItem } from "@mui/material";
import { orderAutoCompleteProvider } from "commons/ultilities/dynamic-form/auto-complete/providers/order-provider";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback, useEffect, useMemo } from "react";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { XTextField } from "share-components/mui-wrap/text-field/x-text-field";
import {
  isPurchaseOrderCompatibleWithSelection,
  isStatusCompatibleWithSelection,
  isWoBatchNumberCompatibleWithSelection,
  isWoBatchNumberCompatibleWithWo,
  isWoNumberCompatibleWithBatch,
  isWoNumberCompatibleWithSelection,
  searchPurchaseOrderOptionsWithPagination,
  searchWoBatchNumberOptionsWithPagination,
  searchWoNumberOptionsWithPagination,
} from "./work-order-po-filter-options";

export type WorkOrderPoGroupSelectionKey =
  | "wONumber"
  | "wOBatchNumber"
  | "purchaseOrderId"
  | "statusId"
  | "isCompensating";

type WorkOrderPoGroupSelectionProps = {
  purchaseOrderIdSelected: string;
  statusIdSelected: string;
  woNumberSelected: string;
  woBatchNumberSelected: string;
  setSelectionProp: (
    prop: WorkOrderPoGroupSelectionKey,
    value: string | boolean,
  ) => void;
  searchWhenRender?: boolean;
  isCompensating?: boolean;
};

export const WorkOrderPoGroupSelection = observer(
  ({
    purchaseOrderIdSelected,
    statusIdSelected,
    woNumberSelected,
    woBatchNumberSelected,
    setSelectionProp,
    searchWhenRender = false,
    isCompensating = false,
  }: WorkOrderPoGroupSelectionProps) => {
    const {
      catalogueStore: {
        workOrderPoStatuses,
        getWorkOrderPoStatuses,
        cataloguesToOptionItems,
      },
    } = useStores();

    const workOrderPoStatusOptionItems = useMemo(() => {
      if (!workOrderPoStatuses) return [];
      return cataloguesToOptionItems(workOrderPoStatuses);
    }, [cataloguesToOptionItems, workOrderPoStatuses]);

    useEffect(() => {
      getWorkOrderPoStatuses();
    }, [getWorkOrderPoStatuses]);

    const searchPurchaseOrderOptionsHandler = useCallback(
      async (
        keyword: string,
        request: { pageIndex: number; pageSize: number },
      ) => {
        const res = await searchPurchaseOrderOptionsWithPagination(
          keyword,
          request,
          statusIdSelected,
          woNumberSelected,
          woBatchNumberSelected,
        );

        return {
          items: res?.items ?? [],
          totalCount: res?.totalCount ?? 0,
        };
      },
      [statusIdSelected, woBatchNumberSelected, woNumberSelected],
    );

    const resolvePurchaseOrderOptionAsync = useCallback(
      async (value: string | number) => {
        return (
          (await orderAutoCompleteProvider.resolveSelectedOptionAsync?.(
            value,
          )) ?? null
        );
      },
      [],
    );

    const woNumberChangedHandler = useCallback(
      async (value: string) => {
        setSelectionProp("wONumber", value);

        if (!value) return;

        if (purchaseOrderIdSelected) {
          const isPurchaseOrderCompatible =
            await isPurchaseOrderCompatibleWithSelection({
              purchaseOrderId: purchaseOrderIdSelected,
              statusId: statusIdSelected,
              wONumber: value,
              wOBatchNumber: woBatchNumberSelected,
            });

          if (!isPurchaseOrderCompatible) {
            setSelectionProp("purchaseOrderId", "");
          }
        }

        if (statusIdSelected) {
          const isStatusCompatible = await isStatusCompatibleWithSelection({
            purchaseOrderId: purchaseOrderIdSelected,
            statusId: statusIdSelected,
            wONumber: value,
            wOBatchNumber: woBatchNumberSelected,
          });

          if (!isStatusCompatible) {
            setSelectionProp("statusId", "");
          }
        }

        if (!woBatchNumberSelected) return;

        const isCompatible = await isWoNumberCompatibleWithBatch(
          value,
          woBatchNumberSelected,
        );

        if (!isCompatible) {
          setSelectionProp("wOBatchNumber", "");
        }
      },
      [
        purchaseOrderIdSelected,
        setSelectionProp,
        statusIdSelected,
        woBatchNumberSelected,
      ],
    );

    const woBatchNumberChangedHandler = useCallback(
      async (value: string) => {
        setSelectionProp("wOBatchNumber", value);

        if (!value) return;

        if (purchaseOrderIdSelected) {
          const isPurchaseOrderCompatible =
            await isPurchaseOrderCompatibleWithSelection({
              purchaseOrderId: purchaseOrderIdSelected,
              statusId: statusIdSelected,
              wONumber: woNumberSelected,
              wOBatchNumber: value,
            });

          if (!isPurchaseOrderCompatible) {
            setSelectionProp("purchaseOrderId", "");
          }
        }

        if (statusIdSelected) {
          const isStatusCompatible = await isStatusCompatibleWithSelection({
            purchaseOrderId: purchaseOrderIdSelected,
            statusId: statusIdSelected,
            wONumber: woNumberSelected,
            wOBatchNumber: value,
          });

          if (!isStatusCompatible) {
            setSelectionProp("statusId", "");
          }
        }

        if (!woNumberSelected) return;

        const isCompatible = await isWoBatchNumberCompatibleWithWo(
          value,
          woNumberSelected,
        );

        if (!isCompatible) {
          setSelectionProp("wONumber", "");
        }
      },
      [
        purchaseOrderIdSelected,
        setSelectionProp,
        statusIdSelected,
        woNumberSelected,
      ],
    );

    const purchaseOrderChangedHandler = useCallback(
      async (value: string) => {
        setSelectionProp("purchaseOrderId", value);

        if (!value) return;

        if (woNumberSelected) {
          const isWoNumberCompatible = await isWoNumberCompatibleWithSelection({
            purchaseOrderId: value,
            statusId: statusIdSelected,
            wONumber: woNumberSelected,
            wOBatchNumber: woBatchNumberSelected,
          });

          if (!isWoNumberCompatible) {
            setSelectionProp("wONumber", "");
          }
        }

        if (woBatchNumberSelected) {
          const isWoBatchCompatible =
            await isWoBatchNumberCompatibleWithSelection({
              purchaseOrderId: value,
              statusId: statusIdSelected,
              wONumber: woNumberSelected,
              wOBatchNumber: woBatchNumberSelected,
            });

          if (!isWoBatchCompatible) {
            setSelectionProp("wOBatchNumber", "");
          }
        }
      },
      [
        setSelectionProp,
        statusIdSelected,
        woBatchNumberSelected,
        woNumberSelected,
      ],
    );

    const statusChangedHandler = useCallback(
      async (value: string) => {
        setSelectionProp("statusId", value);

        if (!value) return;

        if (purchaseOrderIdSelected) {
          const isPurchaseOrderCompatible =
            await isPurchaseOrderCompatibleWithSelection({
              purchaseOrderId: purchaseOrderIdSelected,
              statusId: value,
              wONumber: woNumberSelected,
              wOBatchNumber: woBatchNumberSelected,
            });

          if (!isPurchaseOrderCompatible) {
            setSelectionProp("purchaseOrderId", "");
          }
        }

        if (woNumberSelected) {
          const isWoNumberCompatible = await isWoNumberCompatibleWithSelection({
            purchaseOrderId: purchaseOrderIdSelected,
            statusId: value,
            wONumber: woNumberSelected,
            wOBatchNumber: woBatchNumberSelected,
          });

          if (!isWoNumberCompatible) {
            setSelectionProp("wONumber", "");
          }
        }

        if (woBatchNumberSelected) {
          const isWoBatchCompatible =
            await isWoBatchNumberCompatibleWithSelection({
              purchaseOrderId: purchaseOrderIdSelected,
              statusId: value,
              wONumber: woNumberSelected,
              wOBatchNumber: woBatchNumberSelected,
            });

          if (!isWoBatchCompatible) {
            setSelectionProp("wOBatchNumber", "");
          }
        }
      },
      [
        purchaseOrderIdSelected,
        setSelectionProp,
        woBatchNumberSelected,
        woNumberSelected,
      ],
    );

    return (
      <>
        <Box sx={{ minWidth: 220 }}>
          <XAutoCompleteWithPagination
            label="Số Lệnh S/X"
            value={woNumberSelected}
            searchFunc={(keyword, request) =>
              searchWoNumberOptionsWithPagination(
                keyword,
                request,
                purchaseOrderIdSelected,
                statusIdSelected,
                woBatchNumberSelected,
              )
            }
            searchOnFocus={true}
            searchWhenRender={searchWhenRender}
            onValueChanged={(v) => {
              void woNumberChangedHandler(v?.value?.toString() ?? "");
            }}
          />
        </Box>

        <Box sx={{ minWidth: 220 }}>
          <XAutoCompleteWithPagination
            label="Số Lô S/X"
            value={woBatchNumberSelected}
            searchFunc={(keyword, request) =>
              searchWoBatchNumberOptionsWithPagination(
                keyword,
                request,
                purchaseOrderIdSelected,
                statusIdSelected,
                woNumberSelected,
              )
            }
            searchOnFocus={true}
            searchWhenRender={searchWhenRender}
            onValueChanged={(v) => {
              void woBatchNumberChangedHandler(v?.value?.toString() ?? "");
            }}
          />
        </Box>

        <Box sx={{ minWidth: 220 }}>
          <XAutoCompleteWithPagination
            label="Số Đơn Hàng"
            value={purchaseOrderIdSelected}
            searchFunc={searchPurchaseOrderOptionsHandler}
            searchOnFocus={true}
            searchWhenRender={searchWhenRender}
            resolveSelectedOptionAsync={resolvePurchaseOrderOptionAsync}
            onValueChanged={(v) => {
              void purchaseOrderChangedHandler(v?.value?.toString() ?? "");
            }}
          />
        </Box>

        <Box sx={{ minWidth: 220 }}>
          <XTextField
            fullWidth
            label="Trạng thái"
            value={statusIdSelected}
            select={true}
            onChange={(e) => {
              void statusChangedHandler(e.target.value);
            }}
          >
            <MenuItem value="">
              <em>Tất cả</em>
            </MenuItem>
            {workOrderPoStatusOptionItems.map((x) => (
              <MenuItem key={x.value} value={x.value}>
                {x.label}
              </MenuItem>
            ))}
          </XTextField>
        </Box>

        <Box sx={{ marginLeft: "15px" }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isCompensating}
                onChange={(
                  event: React.ChangeEvent<HTMLInputElement>,
                  checked: boolean,
                ) => setSelectionProp("isCompensating", checked)}
              />
            }
            label="Bù hàng"
          />
        </Box>
      </>
    );
  },
);
